import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import {
  tillSessionService,
  TillSessionDTO,
  DenominationEntry,
} from '../../../../services/tillSessionService';
import {
  Landmark, User, Clock, CalendarCheck, Coins,
  TrendingUp, Scale, ArrowLeft, CheckCircle2,
} from 'lucide-react';
import './TillPage.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtCurrency(amount?: number | null): string {
  if (amount === null || amount === undefined) return '—';
  return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDatetime(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function denomLabel(d: number): string {
  return d >= 1 ? d.toLocaleString() : d.toFixed(2);
}

// ─── Denomination breakdown table ─────────────────────────────────────────────

const DenomTable: React.FC<{ entries: DenominationEntry[]; t: (k: string) => string }> = ({ entries, t }) => (
  <table className="till-denom-table">
    <thead>
      <tr>
        <th>{t('till.denomination')}</th>
        <th className="th-right">{t('till.count')}</th>
        <th className="th-right">{t('till.subtotal')}</th>
      </tr>
    </thead>
    <tbody>
      {entries.map(row => (
        <tr key={row.denomination}>
          <td>
            <span className={`till-denom-label ${row.denomination >= 100 ? 'till-denom-label--note' : 'till-denom-label--coin'}`}>
              {denomLabel(row.denomination)}
            </span>
          </td>
          <td className="td-right">{row.count}</td>
          <td className="td-right">
            <span className="till-subtotal">
              {row.subtotal > 0 ? fmtCurrency(row.subtotal) : '—'}
            </span>
          </td>
        </tr>
      ))}
    </tbody>
    <tfoot>
      <tr className="till-total-row">
        <td colSpan={2}>{t('till.total')}</td>
        <td className="td-right">{fmtCurrency(entries.reduce((s, r) => s + r.subtotal, 0))}</td>
      </tr>
    </tfoot>
  </table>
);

// ─── Component ────────────────────────────────────────────────────────────────

const TillDetailPage: React.FC = () => {
  const { id }   = useParams<{ id: string }>();
  const { t }    = useTranslation();
  const navigate = useNavigate();

  const [session, setSession] = useState<TillSessionDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    tillSessionService.getSessionById(id)
      .then(res => {
        const data = res.data?.data;
        if (data) setSession(data);
        else setError(t('till.notFound'));
      })
      .catch(() => setError(t('till.failedToLoad')))
      .finally(() => setLoading(false));
  }, [id, t]);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Layout>
        <div className="till-detail-page till-page" style={{ alignItems: 'center', justifyContent: 'center' }}>
          <div className="ims-loading-spinner" />
        </div>
      </Layout>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error || !session) {
    return (
      <Layout>
        <div className="till-detail-page till-page">
          <div className="ims-alert ims-alert-error">{error ?? t('till.notFound')}</div>
          <button className="till-hero-back-btn" style={{ marginTop: 16, width: 'fit-content' }} onClick={() => navigate(-1)}>
            <ArrowLeft size={14} strokeWidth={2} /> {t('common.back')}
          </button>
        </div>
      </Layout>
    );
  }

  const isClosed = session.status === 'Closed';

  const variance      = session.variance;
  const varianceClass =
    variance === null || variance === undefined ? ''
    : Math.abs(variance) <= 0.01 ? 'till-variance--ok'
    : variance > 0               ? 'till-variance--over'
    :                              'till-variance--short';

  const varianceLabel =
    variance === null || variance === undefined ? '—'
    : Math.abs(variance) <= 0.01 ? t('till.varianceOk')
    : variance > 0
      ? t('till.varianceOver',  { amount: fmtCurrency(Math.abs(variance)) })
      : t('till.varianceShort', { amount: fmtCurrency(Math.abs(variance)) });

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="till-detail-page till-page">

        {/* ── Glass amber hero ──────────────────────────────────────── */}
        <div className="till-hero-glass">
          <div className="till-hero-left">
            <h1 className="till-hero-title">
              <Landmark size={22} strokeWidth={1.8} />
              {session.sessionNumber}
            </h1>
            <p className="till-hero-sub">
              <User size={13} strokeWidth={2} />
              {session.cashierName}
              &nbsp;·&nbsp;
              <Clock size={13} strokeWidth={2} />
              {fmtDatetime(session.openedAt)}
            </p>
            <div>
              <span className={`till-hero-badge ${isClosed ? 'till-hero-badge--closed' : 'till-hero-badge--open'}`}>
                {t(`till.status${session.status}`)}
              </span>
            </div>
          </div>

          <div className="till-hero-right">
            <button className="till-hero-back-btn" onClick={() => navigate(-1)}>
              <ArrowLeft size={14} strokeWidth={2} />
              {t('common.back')}
            </button>
          </div>
        </div>

        {/* ── KPI stat cards ────────────────────────────────────────── */}
        <div className="ims-stats-row" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))' }}>

          <div className="ims-stat-card">
            <div className="ims-stat-icon till-icon-amber"><Coins size={20} strokeWidth={1.6} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value" style={{ fontSize: '1.05rem' }}>
                {fmtCurrency(session.openingBalance)}
              </span>
              <span className="ims-stat-label">{t('till.openingBalance')}</span>
            </div>
          </div>

          <div className="ims-stat-card">
            <div className="ims-stat-icon till-icon-green"><TrendingUp size={20} strokeWidth={1.6} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value" style={{ fontSize: '1.05rem' }}>
                {fmtCurrency(session.currentCashTotal)}
              </span>
              <span className="ims-stat-label">{t('till.cashReceivedThisSession')}</span>
            </div>
          </div>

          <div className="ims-stat-card">
            <div className="ims-stat-icon till-icon-blue"><Scale size={20} strokeWidth={1.6} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value" style={{ fontSize: '1.05rem' }}>
                {fmtCurrency(isClosed ? session.expectedClosingBalance : session.currentExpectedBalance)}
              </span>
              <span className="ims-stat-label">
                {isClosed ? t('till.expectedClosingBalance') : t('till.currentExpectedBalance')}
              </span>
            </div>
          </div>

          {isClosed && (
            <div className="ims-stat-card">
              <div className="ims-stat-icon till-icon-indigo"><CheckCircle2 size={20} strokeWidth={1.6} /></div>
              <div className="ims-stat-info">
                <span className="ims-stat-value" style={{ fontSize: '1.05rem' }}>
                  {fmtCurrency(session.declaredClosingBalance)}
                </span>
                <span className="ims-stat-label">{t('till.declaredClosingBalance')}</span>
              </div>
            </div>
          )}

          {isClosed && (
            <div className="ims-stat-card">
              <div className={`ims-stat-icon ${
                variance === null || variance === undefined ? 'till-icon-neutral'
                : Math.abs(variance) <= 0.01              ? 'till-icon-green'
                : variance > 0                            ? 'till-icon-blue'
                :                                           'till-icon-red'
              }`}>
                <Scale size={20} strokeWidth={1.6} />
              </div>
              <div className="ims-stat-info">
                <span className={`ims-stat-value ${varianceClass}`} style={{ fontSize: '1.05rem' }}>
                  {varianceLabel}
                </span>
                <span className="ims-stat-label">{t('till.variance')}</span>
              </div>
            </div>
          )}

          <div className="ims-stat-card">
            <div className={`ims-stat-icon ${isClosed ? 'till-icon-neutral' : 'till-icon-green'}`}>
              <CalendarCheck size={20} strokeWidth={1.6} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value" style={{ fontSize: '0.95rem' }}>
                {isClosed ? fmtDatetime(session.closedAt) : t('till.statusOpen')}
              </span>
              <span className="ims-stat-label">{isClosed ? t('till.closedAt') : t('till.status')}</span>
            </div>
          </div>

        </div>

        {/* ── Two-column body ───────────────────────────────────────── */}
        <div className="till-detail-body">

          {/* Left — denomination tables */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

            <div className="ims-card">
              <div className="ims-card-header">
                <span className="ims-card-title">
                  <Coins size={14} strokeWidth={2} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                  {t('till.openingDenominations')}
                </span>
              </div>
              <div className="ims-card-body" style={{ padding: '12px 0 0' }}>
                <DenomTable entries={session.openingDenominations ?? []} t={t} />
              </div>
            </div>

            {isClosed && (session.closingDenominations?.length ?? 0) > 0 && (
              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title">
                    <Coins size={14} strokeWidth={2} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                    {t('till.closingDenominations')}
                  </span>
                </div>
                <div className="ims-card-body" style={{ padding: '12px 0 0' }}>
                  <DenomTable entries={session.closingDenominations!} t={t} />
                </div>
              </div>
            )}
          </div>

          {/* Right — session info + balance / reconciliation */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

            {/* Session metadata */}
            <div className="ims-card">
              <div className="ims-card-header">
                <span className="ims-card-title">
                  <Landmark size={14} strokeWidth={2} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                  {t('till.sessionInfo')}
                </span>
              </div>
              <div className="ims-info-grid">
                <div className="ims-info-field">
                  <span className="ims-info-label">{t('till.sessionNumber')}</span>
                  <span className="ims-info-value">{session.sessionNumber}</span>
                </div>
                <div className="ims-info-field">
                  <span className="ims-info-label">{t('till.cashier')}</span>
                  <span className="ims-info-value">{session.cashierName}</span>
                </div>
                <div className="ims-info-field">
                  <span className="ims-info-label">{t('till.openedAt')}</span>
                  <span className="ims-info-value">{fmtDatetime(session.openedAt)}</span>
                </div>
                {isClosed && (
                  <div className="ims-info-field">
                    <span className="ims-info-label">{t('till.closedAt')}</span>
                    <span className="ims-info-value">{fmtDatetime(session.closedAt)}</span>
                  </div>
                )}
                {session.notes && (
                  <div className="ims-info-field" style={{ gridColumn: '1 / -1' }}>
                    <span className="ims-info-label">{t('till.notes')}</span>
                    <span className="ims-info-value">{session.notes}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Live balance — open sessions */}
            {!isClosed && (
              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title">
                    <TrendingUp size={14} strokeWidth={2} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                    {t('till.liveCashBalance')}
                  </span>
                </div>
                <div className="ims-card-body">
                  <div className="till-summary-card">
                    <div className="till-summary-row">
                      <span className="till-summary-label">{t('till.openingBalance')}</span>
                      <span className="till-summary-value">{fmtCurrency(session.openingBalance)}</span>
                    </div>
                    <div className="till-summary-row">
                      <span className="till-summary-label">{t('till.cashReceivedThisSession')}</span>
                      <span className="till-summary-value" style={{ color: 'var(--ims-green-text)', fontWeight: 700 }}>
                        + {fmtCurrency(session.currentCashTotal)}
                      </span>
                    </div>
                    <hr className="till-summary-divider" />
                    <div className="till-summary-row" style={{ fontWeight: 700 }}>
                      <span className="till-summary-label">{t('till.currentExpectedBalance')}</span>
                      <span className="till-summary-value" style={{ fontSize: 18, color: 'var(--ims-raw-blue-600)' }}>
                        {fmtCurrency(session.currentExpectedBalance)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Reconciliation — closed sessions */}
            {isClosed && (
              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title">
                    <Scale size={14} strokeWidth={2} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                    {t('till.reconciliation')}
                  </span>
                </div>
                <div className="ims-card-body">
                  <div className="till-summary-card">
                    <div className="till-summary-row">
                      <span className="till-summary-label">{t('till.openingBalance')}</span>
                      <span className="till-summary-value">{fmtCurrency(session.openingBalance)}</span>
                    </div>
                    <div className="till-summary-row">
                      <span className="till-summary-label">{t('till.cashReceivedThisSession')}</span>
                      <span className="till-summary-value">+ {fmtCurrency(session.currentCashTotal)}</span>
                    </div>
                    <div className="till-summary-row">
                      <span className="till-summary-label">{t('till.expectedClosingBalance')}</span>
                      <span className="till-summary-value">{fmtCurrency(session.expectedClosingBalance)}</span>
                    </div>
                    <hr className="till-summary-divider" />
                    <div className="till-summary-row">
                      <span className="till-summary-label">{t('till.declaredClosingBalance')}</span>
                      <span className="till-summary-value">{fmtCurrency(session.declaredClosingBalance)}</span>
                    </div>
                    <div className="till-summary-row">
                      <span className="till-summary-label">{t('till.variance')}</span>
                      <span className={`till-summary-value ${varianceClass}`}>{varianceLabel}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>

      </div>
    </Layout>
  );
};

export default TillDetailPage;
